import { Link } from "react-router-dom";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import {
  Gift,
  Check,
  ArrowRight,
  ChevronRight,
  Sparkles,
  Wallet,
  Store,
  LifeBuoy,
  TrendingUp,
  ClipboardList,
  Search,
  Boxes,
  Wrench,
  Monitor,
} from "lucide-react";
import CTASection from "@/components/CTASection";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import {
  EcosystemIllustration,
  GrowthIllustration,
  ProcessIllustration,
  ContactIllustration,
} from "@/components/illustrations/FintechIllustrations";

/* ─── DATA ───────────────────────────────────────────────── */
const BENEFITS = [
  {
    icon: Gift,
    title: "Up to $3,000 in Free Equipment",
    text:
      "Eligible businesses may qualify for valuable POS equipment without a major upfront purchase.",
  },
  {
    icon: Monitor,
    title: "Modern Checkout Tools",
    text:
      "Get access to POS technology designed for smoother transactions and a better customer experience.",
  },
  {
    icon: Wallet,
    title: "Lower Upfront Costs",
    text:
      "Avoid a large initial equipment investment and keep more cash available for your business.",
  },
  {
    icon: Store,
    title: "Built for Real Businesses",
    text:
      "Solutions designed for restaurants, delis, convenience stores, retailers, salons, service businesses, and more.",
  },
  {
    icon: LifeBuoy,
    title: "Setup & Support Included",
    text:
      "Swipetronics helps with setup, guidance, and ongoing support so the process feels simple.",
  },
  {
    icon: TrendingUp,
    title: "Designed to Help You Grow",
    text:
      "A better POS system can help improve efficiency, speed up checkout, and support daily operations.",
  },
];

const ELIGIBILITY = [
  "Businesses processing card payments regularly",
  "Businesses looking to upgrade outdated equipment",
  "Businesses opening or expanding locations",
  "Merchants wanting a more modern POS setup",
  "Businesses interested in reducing upfront equipment expenses",
];

const STEPS = [
  {
    icon: ClipboardList,
    title: "Tell Us About Your Business",
    text: "Share basic information about your business, payment needs, and current setup.",
  },
  {
    icon: Search,
    title: "We Review Eligibility",
    text: "Swipetronics reviews your business type, equipment needs, and processing profile.",
  },
  {
    icon: Boxes,
    title: "Choose the Right POS Setup",
    text: "If eligible, we help match your business with the right POS system and equipment.",
  },
  {
    icon: Wrench,
    title: "Get Set Up",
    text:
      "We help with setup, onboarding, and support so you can start using your new system with confidence.",
  },
];

/* ─── REUSABLE ILLUSTRATION WRAPPER ─────────────────────── */
function IllustrationFrame({ Component, side = "left" }) {
  return (
    <div className="relative">
      <div
        aria-hidden="true"
        className="absolute -inset-4 rounded-[40px] pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 70% 70% at ${side === "left" ? "30%" : "70%"} 50%, rgba(0,114,255,0.18) 0%, transparent 70%)`,
        }}
      />
      <ParticleField
        density={24}
        connectDistance={110}
        className="absolute inset-0 w-full h-full rounded-[40px]"
      />
      <Component className="relative w-full h-auto max-w-[520px] mx-auto drop-shadow-[0_30px_60px_rgba(6,26,58,0.18)]" />
    </div>
  );
}

/* ─── BENEFIT BOX ────────────────────────────────────────── */
function BenefitBox({ icon: Icon, title, text, index }) {
  const isAccent = index % 2 === 0;
  return (
    <div
      data-testid={`free-pos-benefit-${index}`}
      className="group relative h-full rounded-2xl p-7 border border-[var(--swipe-accent)]/15 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] shadow-[0_10px_30px_-18px_rgba(6,26,58,0.18)] hover:shadow-[0_28px_56px_-22px_rgba(0,114,255,0.40)] overflow-hidden"
      style={{
        background: isAccent
          ? "linear-gradient(160deg, #FFFFFF 0%, #EAF3FF 100%)"
          : "linear-gradient(160deg, #EAF3FF 0%, #FFFFFF 100%)",
      }}
    >
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-[3px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
        style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 60%, #061A3A 100%)" }}
      />
      <div
        className="absolute -top-14 -right-14 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: "rgba(0,114,255,0.22)", filter: "blur(44px)" }}
      />
      <div className="relative">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:-rotate-3"
          style={{
            background: isAccent
              ? "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)"
              : "linear-gradient(135deg, #061A3A 0%, #0A2552 100%)",
            boxShadow: isAccent
              ? "0 14px 28px -10px rgba(0,114,255,0.55)"
              : "0 14px 28px -10px rgba(6,26,58,0.55)",
          }}
        >
          <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
        </div>
        <h3 className="mt-6 font-display text-[19px] font-bold text-[var(--swipe-navy)] leading-tight transition-colors duration-300 group-hover:text-[var(--swipe-accent)]">
          {title}
        </h3>
        <p className="mt-3 text-[15px] text-slate-600 leading-[1.7]">{text}</p>
      </div>
    </div>
  );
}

/* ─── MAIN PAGE ──────────────────────────────────────────── */
export default function FreePos() {
  return (
    <div data-testid="free-pos-page">
      <Seo {...getSeoForPath("/free-pos-program")} />
      {/* HERO — unified */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)" }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)" }} />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/products" className="hover:text-white">Products</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">Free POS Program</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  Special Program
                </div>
                <h1 className="font-display-tight text-[2.75rem] sm:text-[3.75rem] lg:text-[5rem] xl:text-[5.5rem] font-extrabold leading-[1.02] text-white uppercase tracking-[-0.035em]">
                  FREE <span className="text-[var(--swipe-accent)]">POS</span> PROGRAM
                </h1>
                <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.7] max-w-xl">
                  Eligible businesses may qualify for a free POS system through the Swipetronics
                  Free POS Program — a modern POS with no large upfront equipment cost when
                  switching to Swipetronics.
                </p>

                {/* highlight pills */}
                <div className="mt-7 flex flex-wrap gap-2.5">
                  {[
                    "Up to $3,000 in free equipment",
                    "Fast setup",
                    "Real support",
                  ].map((p) => (
                    <span
                      key={p}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-[11.5px] font-bold text-white"
                    >
                      <Sparkles className="w-3 h-3 text-[var(--swipe-accent)]" />
                      {p}
                    </span>
                  ))}
                </div>

                <div className="mt-9 flex flex-wrap gap-3">
                  <Link to="/savings-analysis" className="btn-primary-swipe text-[15px] py-3.5 px-7" data-testid="free-pos-hero-qualify">
                    See If I Qualify <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/get-started" className="btn-outline-light text-[15px] py-3.5 px-7" data-testid="free-pos-hero-contact">
                    Get Started
                  </Link>
                </div>
                <p className="mt-5 text-[12px] text-white/45 max-w-md leading-relaxed">
                  Subject to approval and program terms. Qualification depends on business type,
                  processing volume, equipment needs, approval, and underwriting.
                </p>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <EcosystemIllustration transparent className="relative w-full h-auto max-w-[560px] mx-auto" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALUE SECTION — text LEFT, illustration RIGHT */}
      <section className="section bg-white">
        <div className="container-swipe">
          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-12 lg:gap-16 items-center">
            <div>
              <Reveal>
                <div className="overline mb-4">Why Business Owners Are Interested</div>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                  Get the Tools You Need Without a Big Upfront Cost.
                </h2>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-6 text-[17px] text-slate-600 leading-[1.8] max-w-xl">
                  Swipetronics helps eligible businesses upgrade to a better checkout experience
                  with POS equipment that supports payments, order flow, reporting, and day-to-day
                  operations. Instead of paying a large amount upfront for equipment, qualifying
                  businesses may be able to access a Free POS Program designed to lower startup
                  costs and simplify the switch.
                </p>
              </Reveal>
              <Reveal delay={240}>
                <div className="mt-7 flex flex-wrap gap-2.5">
                  {["Restaurants", "Retail", "Convenience", "Salons", "Service"].map((t) => (
                    <span key={t} className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] text-[12px] font-bold uppercase tracking-wider border border-[var(--swipe-accent)]/15">
                      {t}
                    </span>
                  ))}
                </div>
              </Reveal>
            </div>
            <Reveal variant="slide-right" delay={120}>
              <IllustrationFrame Component={GrowthIllustration} side="right" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* BENEFITS — 6 colorful boxes */}
      <section className="bg-[var(--swipe-accent-soft)]/40 border-y border-[var(--swipe-accent)]/15">
        <div className="container-swipe py-20 lg:py-24">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-14">
              <div className="overline mb-3 inline-flex justify-center">Program Benefits</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.1]">
                Modern POS technology, lower upfront cost.
              </h2>
              <p className="mt-5 text-[16px] text-slate-600 leading-[1.75] max-w-[780px] mx-auto">
                A program built around the realities of running a business — designed to help
                qualifying owners upgrade their checkout without a big equipment investment.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={(i % 3) * 80}>
                <BenefitBox icon={b.icon} title={b.title} text={b.text} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ELIGIBILITY — illustration LEFT, copy RIGHT */}
      <section className="section bg-white">
        <div className="container-swipe">
          <div className="grid lg:grid-cols-[1fr,1.05fr] gap-12 lg:gap-16 items-center">
            <Reveal variant="slide-left">
              <IllustrationFrame Component={ProcessIllustration} side="left" />
            </Reveal>
            <div>
              <Reveal>
                <div className="overline mb-4">Who May Qualify?</div>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                  Built for businesses ready to upgrade.
                </h2>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-6 text-[17px] text-slate-600 leading-[1.8] max-w-xl">
                  Free POS Program eligibility depends on business type, monthly processing volume,
                  equipment needs, approval, and underwriting. Swipetronics reviews your business
                  and recommends the right setup based on your goals and operations.
                </p>
              </Reveal>
              <Reveal delay={240}>
                <ul className="mt-7 space-y-3">
                  {ELIGIBILITY.map((e) => (
                    <li key={e} className="flex items-start gap-3 text-[15px] text-slate-700">
                      <span className="mt-1 w-5 h-5 rounded-full bg-[var(--swipe-accent)] text-white flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3" strokeWidth={3.5} />
                      </span>
                      {e}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 4 numbered cards */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden">
        <div className="absolute inset-0 grid-faint opacity-25 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(0,114,255,0.22) 0%, transparent 65%)" }} />

        <div className="container-swipe relative py-20 lg:py-24">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-14">
              <div className="overline mb-3 inline-flex justify-center" style={{ color: "#7DB6FF" }}>How It Works</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold leading-[1.1] text-white">
                A simple path from inquiry to install.
              </h2>
              <p className="mt-5 text-[16px] text-white/70 leading-[1.75] max-w-[780px] mx-auto">
                Four straightforward steps with a real person guiding you through each one — no
                pressure, no commitments, no fine-print surprises.
              </p>
            </div>
          </Reveal>

          <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            <div aria-hidden="true" className="hidden lg:block absolute top-[60px] left-[10%] right-[10%] h-[2px] pointer-events-none" style={{ background: "repeating-linear-gradient(90deg, rgba(125,182,255,0.4) 0 8px, transparent 8px 16px)" }} />
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <Reveal key={step.title} delay={i * 90}>
                  <div
                    className="relative h-full rounded-2xl p-6 lg:p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] border border-white/10 backdrop-blur"
                    style={{ background: "rgba(255,255,255,0.04)" }}
                    data-testid={`free-pos-step-${i + 1}`}
                  >
                    <div className="relative mx-auto w-[112px] h-[112px] flex items-center justify-center mb-5">
                      <div className="absolute inset-0 rounded-full" style={{ background: "conic-gradient(from 180deg, rgba(0,114,255,0.40), rgba(0,114,255,0.0) 60%)" }} />
                      <div className="relative w-[92px] h-[92px] rounded-full flex items-center justify-center bg-[var(--swipe-navy)]" style={{ boxShadow: "inset 0 0 0 2px rgba(0,114,255,0.5), 0 18px 36px -16px rgba(0,114,255,0.55)" }}>
                        <Icon className="w-8 h-8 text-[var(--swipe-accent)]" strokeWidth={2} />
                      </div>
                      <span
                        className="absolute -top-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-extrabold text-white"
                        style={{ background: "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)", boxShadow: "0 10px 22px -8px rgba(0,114,255,0.65)" }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="font-display-tight text-[17px] lg:text-[18px] font-extrabold leading-tight text-white text-center">
                      {step.title}
                    </h3>
                    <p className="mt-3 text-[14px] text-white/65 leading-[1.7] text-center">{step.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA — illustration LEFT, copy RIGHT */}
      <section className="section bg-white">
        <div className="container-swipe">
          <div className="grid lg:grid-cols-[1fr,1.05fr] gap-12 lg:gap-16 items-center">
            <Reveal variant="slide-left">
              <IllustrationFrame Component={ContactIllustration} side="left" />
            </Reveal>
            <div>
              <Reveal>
                <div className="overline mb-4">Take the next step</div>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                  See If Your Business Qualifies.
                </h2>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-6 text-[17px] text-slate-600 leading-[1.8] max-w-xl">
                  Find out if your business may qualify for the Swipetronics Free POS Program and
                  up to <span className="font-bold text-[var(--swipe-navy)]">$3,000 in free equipment</span>.
                  No commitment — just an honest review of your setup and a clear answer.
                </p>
              </Reveal>
              <Reveal delay={240}>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link to="/savings-analysis" className="btn-primary-swipe text-[15px] py-3.5 px-7" data-testid="free-pos-cta-qualify">
                    See If I Qualify <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/get-started" className="btn-outline text-[15px] py-3.5 px-7" data-testid="free-pos-cta-contact">
                    Get Started
                  </Link>
                </div>
              </Reveal>
              <p className="mt-5 text-[12px] text-slate-500 max-w-md leading-relaxed">
                Subject to approval and program terms. Qualification depends on business type,
                processing volume, equipment needs, approval, and underwriting.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Free POS Program"
        title="Ready to upgrade your checkout?"
        description="A quick conversation with a Swipetronics specialist is the easiest way to find out if your business may qualify."
      />
    </div>
  );
}
